export interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  vendorType?: VendorType;
  razorpayLinkedAccountId?: string;
  vendorBankDetails?: {
    accountHolderName?: string;
    accountNumber?: string;
    ifsc?: string;
    bankName?: string;
  };
  vendorUpiId?: string;
  isActive: boolean;
  isEmailVerified: boolean;
  lastLogin?: string;
  isFirstTimeLogin: boolean;
  profile: UserProfile;
  createdAt: string;
  updatedAt: string;
  fullName: string;
}

export type UserRole = 'user' | 'vendor' | 'admin';

export type VendorType = 'service_vendor' | 'product_vendor' | 'general_vendor';

export interface UserProfile {
  avatar?: string;
  bio?: string;
  address?: UserAddress;
  dateOfBirth?: string;
}

export interface UserAddress {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

/**
 * Auth state for Redux store
 * Note: Tokens are stored in HTTP-only cookies, not in client-side state
 */
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requirePasswordChange: boolean;
  requireEmailVerification: boolean;
  pendingOtp: { tempToken: string } | null;
  pendingRegisterOtp: { tempToken: string } | null;
}

export interface LoginRequest {
  email?: string;
  phone?: string;
}

export interface RegisterRequest {
  email?: string;
  firstName: string;
  lastName: string;
  phone: string;
  vendorType?: VendorType;
}

export interface ForgotPasswordRequest {
  phone: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface DeleteAccountRequest {
  otp: string;
}

export interface FirstTimePasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  profile?: Partial<UserProfile>;
}

export interface CreateVendorRequest {
  email: string;
  firstName: string;
  lastName: string;
  vendorType: VendorType;
  phone?: string;
  tempPassword: string;
}

export interface CreateAdminRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  tempPassword: string;
}

/**
 * Auth response from backend
 * Note: Tokens are set as HTTP-only cookies by the backend, not returned in response body
 */
export interface AuthResponse {
  user: User;
  requirePasswordChange?: boolean;
  emailVerificationSent?: boolean;
}

/**
 * When login requires OTP, backend returns this instead of user + tokens
 */
export interface LoginOtpRequiredResponse {
  requiresOtp: true;
  tempToken: string;
  message: string;
}

export type LoginResponse = AuthResponse | LoginOtpRequiredResponse;

/**
 * When registration requires OTP, backend returns this instead of user + tokens
 */
export interface RegisterOtpRequiredResponse {
  requiresOtp: true;
  tempToken: string;
  message: string;
}

/**
 * Profile response from backend
 */
export interface ProfileResponse {
  user: User;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export interface AuthError {
  message: string;
  statusCode: number;
  field?: string;
  requirePasswordChange?: boolean;
  requireEmailVerification?: boolean;
}

export interface LoginFormData {
  emailOrPhone: string;
  rememberMe?: boolean;
}

export interface RegisterFormData {
  firstName: string;
  lastName: string;
  phone: string;
  vendorType?: VendorType | '';
  agreedToTerms: boolean;
}

export interface ForgotPasswordFormData {
  phone: string;
}

export interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileFormData {
  firstName: string;
  lastName: string;
  phone?: string;
  bio?: string;
  dateOfBirth?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
}

export interface VendorFormData {
  firstName: string;
  lastName: string;
  email: string;
  vendorType: VendorType;
  phone?: string;
  tempPassword: string;
}

export interface AdminFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  tempPassword: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
  changeFirstTimePassword: (
    data: FirstTimePasswordChangeRequest
  ) => Promise<void>;
  updateProfile: (data: UpdateProfileRequest) => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}

// Note: Tokens are stored in HTTP-only cookies, not in localStorage
// These keys are kept for backward compatibility during migration
export const AUTH_STORAGE_KEYS = {
  USER: 'dreamoraa_user',
  REMEMBER_ME: 'dreamoraa_remember_me',
} as const;

// API Endpoints
export const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  LOGOUT: '/auth/logout',
  LOGOUT_ALL: '/auth/logout-all',
  REFRESH_TOKEN: '/auth/refresh-token',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  CHANGE_PASSWORD: '/auth/change-password',
  CHANGE_FIRST_TIME_PASSWORD: '/auth/change-first-time-password',
  PROFILE: '/auth/profile',
  UPDATE_PROFILE: '/auth/profile',
  VERIFY_EMAIL: '/auth/verify-email',
  RESEND_VERIFICATION: '/auth/resend-verification',
  CREATE_VENDOR: '/auth/create-vendor',
  CREATE_ADMIN: '/auth/create-admin',
  VERIFY_LOGIN_OTP: '/auth/verify-login-otp',
  VERIFY_REGISTER_OTP: '/auth/verify-register-otp',
  VERIFY_FORGOT_OTP: '/auth/verify-forgot-otp',
  DELETE_ACCOUNT_REQUEST_OTP: '/auth/delete-account/request-otp',
  DELETE_ACCOUNT_CONFIRM_OTP: '/auth/delete-account/confirm-otp',
} as const;

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  LOCKED: 423,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
} as const;
